import Cookies from 'js-cookie';
import gtag from 'components/analytics/conversions/gtag';
import {
  getAnalyticsDimensions,
  TrackingData,
  dataLayerPush,
  TRACKING_EVENT_NAME,
  findCategoryByElement,
  getCompleteEventSources,
  deleteCompleteEventSources,
} from 'components/analytics/conversions/tracking';
import { PAGE_VIEW, TrackingAction } from 'components/analytics/conversions/constants/actions';
import SnowplowTracker from 'components/analytics/conversions/snowplow-tracker';

export type GtagConfig = {
  gaId: string;
  userStatusStart: string;
  userStatusUpdated: string;
  userPlan: string;
  userSurvey: string;
  freeDownloadCount: string;
  purchaseCount: string;
  ltv: string;
  splitTest: string;
  testBlock: string;
  featureFlags: string;
  snowplowCollector: string;
  snowplowAppId: string;
}

type SnowplowPayload = {
  action: string,
  category: string,
  label?: string,
  value?: number,
  contentType?: string,
  errorText?: string,
}

/* eslint-disable camelcase */
interface GtagEventParams extends Gtag.EventParams {
  error_text?: string,
}

type PageViewPayload = {
  action: string;
  page_title: string;
  page_location: string;
  page_path: string;
  user_status_start: string;
  user_status_updated: string;
  user_plan: string;
  user_survey: string;
  free_download_count: number;
  purchase_count: number;
  ltv: number;
  split_test: string;
  test_block: number;
  feature_flags: string;
  label?: string;
  content_type?: string;
  team_owner_email?: string;
  team_seats?: number;
  multiple_logins?: number;
}
/* eslint-enable camelcase */

export default function ConversionTracker() {
  const TURBOLINKS_LOAD_EVENT = 'turbolinks:render';
  let snowplowTracker;

  const sendEvent = (action:TrackingAction, payload:GtagEventParams) => {
    if (document.body.dataset.env === 'test') {
      const googleEventsSent = Cookies.getJSON('googleEventsSent') || [];
      googleEventsSent.push({
        event_action: action,
        ...payload,
      });

      Cookies.set('googleEventsSent', googleEventsSent, { domain: '.example.com' });
    }

    gtag('event', action, payload);

    // copy events to snowplow
    const snowplowPayload: SnowplowPayload = {
      category: payload.event_category,
      action,
    };

    if (Object.hasOwnProperty.call(payload, 'event_label')) snowplowPayload.label = payload.event_label;
    if (Object.hasOwnProperty.call(payload, 'value')) snowplowPayload.value = payload.value;
    if (Object.hasOwnProperty.call(payload, 'content_type')) snowplowPayload.contentType = payload.content_type;
    if (Object.hasOwnProperty.call(payload, 'error_text')) snowplowPayload.errorText = payload.error_text;

    snowplowTracker.trackEvent(snowplowPayload);
  };

  const trackEvent = (event) => {
    const {
      action,
      category,
      label,
      dataLayer,
      ...extraData
    }:TrackingData = event.detail;

    const eventTarget = event.target || document.body;
    const eventCategory = (!category) ? findCategoryByElement(eventTarget) : category;

    const payload:Gtag.EventParams = {
      event_category: eventCategory,
      ...extraData,
    };

    if (label) payload.event_label = label;

    sendEvent(action, payload);

    if (dataLayer) dataLayerPush(dataLayer);
  };

  const goFindValue = (selector, attribute) => {
    const locationElement = document.querySelector(selector);

    return locationElement.getAttribute(`data-${attribute}`);
  };

  const processCompleteEvents = () => {
    const completeEventSources = getCompleteEventSources();

    if (!completeEventSources || !Array.isArray(completeEventSources)) {
      deleteCompleteEventSources();
      return;
    }

    completeEventSources.forEach((event) => {
      if (event.valueElementSelector && event.valueAttribute) {
        const result = goFindValue(event.valueElementSelector, event.valueAttribute);

        event.value = result;

        delete event.valueElementSelector;
        delete event.valueAttribute;
      }

      if (event.dataLayerCompleteAttrs) {
        const analyticsDimensions = getAnalyticsDimensions();
        event.dataLayer = event.dataLayer || [];

        event.dataLayerCompleteAttrs.forEach((attribute) => {
          event.dataLayer[attribute] = analyticsDimensions[attribute] || '';
        });

        delete event.dataLayerCompleteAttrs;
      }

      trackEvent({ detail: event });
    });

    deleteCompleteEventSources();
  };

  const sendPageViewEvent = () => {
    /* eslint-disable camelcase */
    // properties for GA are expected to be in snake_case and come from the server as such
    const {
      user_status_start,
      user_status_updated,
      user_plan,
      user_survey,
      free_download_count,
      purchase_count,
      ltv,
      split_test,
      test_block,
      feature_flags,
      team_owner_email,
      team_seats,
      multiple_logins,
    } = getAnalyticsDimensions();

    const {
      conversionsLabel: label,
      conversionsContentType: content_type,
    } = document.body.dataset;

    const payload: PageViewPayload = {
      action: PAGE_VIEW,
      page_title: document.title,
      page_location: window.location.href,
      page_path: window.location.pathname,
      user_status_start,
      user_status_updated,
      user_plan,
      user_survey,
      free_download_count,
      purchase_count,
      ltv,
      split_test,
      test_block,
      feature_flags,
      team_owner_email,
      team_seats,
      multiple_logins,
    };

    if (label) {
      payload.label = label;
    }

    if (content_type) {
      payload.content_type = content_type;
    }
    /* eslint-enable camelcase */

    trackEvent({
      detail: payload,
    });

    dataLayerPush();
  };

  const pageLoad = () => {
    sendPageViewEvent();
    processCompleteEvents();
  };

  const initSnowplow = () => {
    /* eslint-disable-next-line camelcase */
    const { snowplow_collector, snowplow_app_id } = getAnalyticsDimensions();

    snowplowTracker = new SnowplowTracker({ collector: snowplow_collector, appId: snowplow_app_id });
    snowplowTracker.init();
    snowplowTracker.trackABDetection();
  };

  const freshLoadInit = () => pageLoad();

  this.init = () => {
    initSnowplow();
    window.addEventListener('load', freshLoadInit);
    document.addEventListener(TRACKING_EVENT_NAME, trackEvent);
    document.addEventListener(TURBOLINKS_LOAD_EVENT, pageLoad);
  };

  this.disconnectEvents = () => {
    window.removeEventListener('load', freshLoadInit);
    document.removeEventListener(TRACKING_EVENT_NAME, trackEvent);
    document.removeEventListener(TURBOLINKS_LOAD_EVENT, pageLoad);
  };
}
